import { consumer, core } from '@tock/coretypes';
import { getAppConfig } from 'selectors/appSelector';
import { isEventVariety } from '@tock/shared/utils/offerings';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useSelector } from 'react-redux';
import DisplayPercentage from '@tock/shared/components/number/DisplayPercentage';
import React from 'react';

// This list should be kept in sync with the list in PriceDisplay.java
// https://github.com/tocktix/admin/blob/fbc49eae474ee56ac29ad7b2661fd63cea41c2f2/server/src/main/java/com/tocktix/payments/fees/PriceDisplay.java#L10
const PRICE_DISPLAY_ITEMIZED_STATES = ['CA'];
const PRICE_DISPLAY_MIN_REQUIRED_SPEND_STATES = ['MN'];

/**
 * Determines whether or not to use "minimum required spend"-style price display.
 * This will only work within business context (i.e., business page / checkout / etc.) since it
 * relies on the current business location.
 */
export function useIsMinRequiredSpendDisplayEnabled(ticketTypeVariety?: core.TicketType.Variety) {
  const isOverrideFFEnabled = useFeatureFlag('PRICE_DISPLAY_FORCE_MIN_REQUIRED_SPEND');
  const isForceItemizedPriceDisplayEnabled = useFeatureFlagForConsumer(
    'PRICE_DISPLAY_FORCE_ITEMIZED'
  );
  const isItemizedDisplayOverrideForBusiness = useFeatureFlagForConsumer(
    'ENABLE_BASE_PRICE_SEPARATE_FEE_DISPLAY_OVERRIDE'
  );
  const isMaVaDisplayEnabled = useFeatureFlagForConsumer('JUNK_FEES_MA_VA_DISPLAY');
  const isCoDisplayEnabled = useFeatureFlagForConsumer('JUNK_FEES_CO_ENABLE_DISPLAY');
  const isGlobalDisplayEnabled = useFeatureFlagForConsumer('JUNK_FEES_ENABLE_GLOBAL_ROLLOUT');

  const currentBusinessState = useSelector(getAppConfig)?.business?.state;

  // Events will always use min required spend as of May 2025.
  if (isEventVariety(ticketTypeVariety)) {
    return true;
  }

  // Business-level override to always show itemized fees rather than an all-in price.
  if (isItemizedDisplayOverrideForBusiness) {
    return false;
  }

  if (isForceItemizedPriceDisplayEnabled) {
    return true;
  }

  if (isGlobalDisplayEnabled && currentBusinessState !== 'CA') {
    return true;
  }
  if (isCoDisplayEnabled && currentBusinessState === 'CO') {
    return true;
  }
  if (isMaVaDisplayEnabled && (currentBusinessState === 'MA' || currentBusinessState === 'VA')) {
    return true;
  }
  if (isOverrideFFEnabled) {
    return true;
  }

  return !!(currentBusinessState && PRICE_DISPLAY_ITEMIZED_STATES.includes(currentBusinessState));
}

/**
 * Determines which price display style is relevant based on feature flags and business state
 */
export function usePriceDisplayStyle(
  ticketTypeVariety?: core.TicketType.Variety
): consumer.OfferedPriceSummary.PriceDisplayStyle {
  const isForceMinRequiredSpendPriceDisplayEnabled = useFeatureFlagForConsumer(
    'PRICE_DISPLAY_FORCE_MIN_REQUIRED_SPEND'
  );

  const isForceItemizedPriceDisplayEnabled = useFeatureFlagForConsumer(
    'PRICE_DISPLAY_FORCE_ITEMIZED'
  );

  const isItemizedDisplayOverrideForBusiness = useFeatureFlagForConsumer(
    'ENABLE_BASE_PRICE_SEPARATE_FEE_DISPLAY_OVERRIDE'
  );

  const isMaVaDisplayEnabled = useFeatureFlagForConsumer('JUNK_FEES_MA_VA_DISPLAY');
  const isCoDisplayEnabled = useFeatureFlagForConsumer('JUNK_FEES_CO_ENABLE_DISPLAY');
  const isGlobalDisplayEnabled = useFeatureFlagForConsumer('JUNK_FEES_ENABLE_GLOBAL_ROLLOUT');

  const currentBusinessState = useSelector(getAppConfig)?.business?.state ?? '';
  const effectiveState = isForceItemizedPriceDisplayEnabled ? 'CA' : currentBusinessState;

  // Events will always use min required spend as of May 2025.
  if (isEventVariety(ticketTypeVariety)) {
    return 'MIN_REQUIRED_SPEND';
  }

  // Business-level override to always show itemized fees rather than an all-in price.
  if (isItemizedDisplayOverrideForBusiness) {
    return 'ITEMIZED';
  }

  if (isGlobalDisplayEnabled && effectiveState !== 'CA') {
    return 'MIN_REQUIRED_SPEND';
  }
  if (isCoDisplayEnabled && effectiveState === 'CO') {
    return 'MIN_REQUIRED_SPEND';
  }
  if (isMaVaDisplayEnabled && (effectiveState === 'MA' || effectiveState === 'VA')) {
    return 'MIN_REQUIRED_SPEND';
  }

  // Forcing flag
  if (isForceMinRequiredSpendPriceDisplayEnabled) {
    return 'MIN_REQUIRED_SPEND';
  } else if (isForceItemizedPriceDisplayEnabled) {
    return 'ITEMIZED';
  }

  if (PRICE_DISPLAY_MIN_REQUIRED_SPEND_STATES.includes(effectiveState)) {
    return 'MIN_REQUIRED_SPEND';
  } else if (PRICE_DISPLAY_ITEMIZED_STATES.includes(effectiveState)) {
    return 'ITEMIZED';
  } else {
    return 'BASE_TICKET_PRICE';
  }
}

/**
 * Determines which price style and fee disclaimer label should be displayed pages related to addons
 */
export function useAddonsPriceDisplayDetails(ticketType: core.ITicketType) {
  const priceDisplayStyle = usePriceDisplayStyle(ticketType.variety ?? undefined);
  const useInclusivePricing = priceDisplayStyle === 'MIN_REQUIRED_SPEND';
  const feeDisclaimerLabel =
    priceDisplayStyle === 'MIN_REQUIRED_SPEND'
      ? getFeeDisclaimerLabelForInclusivePricing(ticketType)
      : getFeeDisclaimerLabel(ticketType);
  const showFeeDisclaimerLabel =
    priceDisplayStyle === 'ITEMIZED' || priceDisplayStyle === 'MIN_REQUIRED_SPEND';

  return { useInclusivePricing, showFeeDisclaimerLabel, feeDisclaimerLabel };
}

export function getFeeDisclaimerLabelForInclusivePricing(ticketType: core.ITicketType) {
  const list: string[] = [];
  if (ticketType.customFeeRate && ticketType.customFeeName) {
    list.push(ticketType.customFeeName);
  }
  if (ticketType.extraChargeDetails?.serviceChargeRate) {
    list.push(
      ticketType.extraChargeDetails.extraChargeType === 'SERVICE_CHARGE'
        ? 'Service charge'
        : 'Gratuity'
    );
  }
  if (ticketType.eventFeeRate) {
    list.push('Event fee');
  }

  return list.length === 0 ? '' : `* Included in all item costs: ${list.join(', ')}`;
}

export function getFeeDisclaimerLabel(ticketType: core.ITicketType) {
  const list: React.ReactNode[] = [];
  if (ticketType.customFeeRate && ticketType.customFeeName) {
    list.push(
      <>
        <DisplayPercentage value={ticketType.customFeeRate / 100} />{' '}
        {ticketType.customFeeName.toLocaleLowerCase()}
      </>
    );
  }
  if (ticketType.extraChargeDetails?.serviceChargeRate) {
    list.push(
      <>
        <DisplayPercentage value={ticketType.extraChargeDetails.serviceChargeRate / 100} />{' '}
        {ticketType.extraChargeDetails.serviceChargeName?.toLocaleLowerCase()}
      </>
    );
  }
  if (ticketType.eventFeeRate) {
    list.push(
      <>
        <DisplayPercentage value={ticketType.eventFeeRate / 100} /> {'event fee'}
      </>
    );
  }

  if (list.length === 0) {
    return null;
  }

  return (
    <>
      All prices subject to{' '}
      {list.map((feeDisplay, index) => (
        <>
          {feeDisplay}
          {index < list.length - 1 ? ', ' : ' '}
        </>
      ))}
    </>
  );
}

export function getHasNonZeroEffectivePrice(
  basePriceCents: number,
  minRequiredSpendCents: number,
  priceDisplayStyle: consumer.OfferedPriceSummary.PriceDisplayStyle = 'BASE_TICKET_PRICE'
) {
  const hasNonZeroTicketPrice = basePriceCents > 0;
  const hasNonZeroMinRequiredPrice = minRequiredSpendCents > 0;

  return priceDisplayStyle === 'MIN_REQUIRED_SPEND'
    ? hasNonZeroMinRequiredPrice
    : hasNonZeroTicketPrice;
}
